import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Paper,
  Avatar,
  TextField,
  Modal,
  Chip,
} from "@mui/material";
import { Close, Delete, AccessTime, Send, Image } from "@mui/icons-material";
import useAlert from "../../../../hooks/useAlert";
import { useSelector } from "react-redux";
import userTaskService from "../../../../services/userTaskService";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";

// Task durumları için renk ve açıklamalar
const taskStateConfig = {
  1: { label: "Yeni Destek Talebi", color: "#2196F3", bgColor: "#E3F2FD" },
  2: { label: "Destek Ekibi İnceliyor", color: "#9C27B0", bgColor: "#F3E5F5" },
  3: { label: "Yanıt Bekliyor", color: "#FF9800", bgColor: "#FFF3E0" },
  4: {
    label: "Kullanıcı Yanıtı Bekleniyor",
    color: "#F44336",
    bgColor: "#FFEBEE",
  },
  5: { label: "Çözüldü", color: "#4CAF50", bgColor: "#E8F5E9" },
  6: { label: "Kapatıldı", color: "#607D8B", bgColor: "#ECEFF1" },
  7: { label: "İptal Edildi", color: "#9E9E9E", bgColor: "#F5F5F5" },
};

const MessageContainer = styled(Paper)(({ theme, isMainUser }) => ({
  padding: theme.spacing(2),
  backgroundColor: isMainUser ? "#E3F2FD" : "white",
  borderRadius: "12px",
  border: "none",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  marginLeft: isMainUser ? 0 : theme.spacing(4),
  marginRight: isMainUser ? theme.spacing(4) : 0,
  position: "relative",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  },
  transition: "all 0.2s ease-in-out",
}));

const ImagePreview = styled("img")(({ theme }) => ({
  maxWidth: "250px",
  maxHeight: "180px",
  objectFit: "cover",
  borderRadius: "12px",
  border: "none",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  cursor: "pointer",
  margin: theme.spacing(1),
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "scale(1.03)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
}));

const TaskDetail = ({ open, onClose, task, onTaskDeleted }) => {
  const [taskDetail, setTaskDetail] = useState(null);
  const [subTasks, setSubTasks] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);
  const { showSuccessAlertAllNoReload, showErrorAlertAll } = useAlert();
  const isAdmin = useSelector((state) => state?.auth?.user?.role === "admin");
  const userId = useSelector((state) => state?.auth?.user?.id);
  const user = useSelector((state) => state?.auth?.user);
  const messagesEndRef = useRef(null);

  const loadTaskDetail = async () => {
    try {
      setLoadingDetail(true);

      if (!task || !task.id) {
        console.error("Invalid task object:", task);
        showErrorAlertAll("Geçersiz talep bilgisi.");
        setLoadingDetail(false);
        return;
      }

      const taskDetails = await userTaskService.getUserTaskById(task.id);

      if (!taskDetails || !taskDetails.id) {
        console.error("Invalid task details received:", taskDetails);
        showErrorAlertAll("Talep detayları alınamadı.");
        setLoadingDetail(false);
        return;
      }

      const subTasksResponse = await userTaskService.getSubTasks(task.id);

      // Ana görev detaylarını birleştir
      const processedTask = {
        ...task,
        ...taskDetails,
        id: task.id,
        userId: task.userId || taskDetails.userId,
        title: taskDetails.title || task.title || "Başlıksız Talep",
        content:
          taskDetails.detail ||
          taskDetails.content ||
          task.content ||
          task.detail ||
          "",
        state:
          taskDetails.state !== undefined ? taskDetails.state : task.state || 0,
        createDate:
          taskDetails.createDate || task.createDate || new Date().toISOString(),
        updateDate:
          taskDetails.updateDate || task.updateDate || new Date().toISOString(),
        user: taskDetails.user ||
          task.user || { id: task.userId || taskDetails.userId },
        sub_task: 0, // Ana task için sub_task 0 olmalı
      };

      // Sadece bu task'a ait olan subtask'leri filtrele
      const filteredSubTasks = (subTasksResponse || []).filter(
        (subTask) => subTask.parentId === task.id
      );

      setTaskDetail(processedTask);
      setSubTasks(filteredSubTasks);
      setLoadingDetail(false);
    } catch (error) {
      console.error("Error loading task details:", error);
      console.error("Error details:", error.response?.data || error.message);
      setLoadingDetail(false);
      showErrorAlertAll("Talep detayları yüklenirken bir hata oluştu.");
    }
  };

  useEffect(() => {
    if (open && task?.id) {
      loadTaskDetail();
    }
  }, [open, task?.id]);

  const handleSubmitReply = async () => {
    if (!replyText.trim()) return;

    try {
      setSending(true);
      const replyData = {
        detail: replyText,
        userId: userId,
        parentId: taskDetail.id,
        sub_task: 1,
      };

      const response = await userTaskService.addSubTask(
        taskDetail.id,
        replyData
      );

      const newSubTask = {
        id: response?.data?.id || Date.now(),
        detail: replyText,
        userId: userId,
        parentId: taskDetail.id,
        createDate: new Date().toISOString(),
        sub_task: 1,
        user: {
          name: isAdmin ? "Destek Ekibi" : "Kullanıcı",
          avatarUrl: "",
        },
      };

      setSubTasks((prev) => [...prev, newSubTask]);
      setReplyText("");
      setSending(false);
      scrollToBottom();
    } catch (error) {
      setSending(false);
      showErrorAlertAll("Mesaj gönderilemedi.");
      console.error("Error sending reply:", error);
    }
  };

  const handleDeleteTask = async () => {
    try {
      setDeleting(true);
      // Call the delete service
      const response = await userTaskService.deleteUserTask(taskDetail.id);

      if (response?.status === true) {
        showSuccessAlertAllNoReload("Talep başarıyla silindi.");
        if (onTaskDeleted) {
          onTaskDeleted();
        }
        setDeleting(false);
        setDeleteDialogOpen(false);
        onClose();
      } else {
        throw new Error("Silme işlemi başarısız oldu");
      }
    } catch (error) {
      setDeleting(false);
      showErrorAlertAll(
        "Talep silinirken bir hata oluştu: " +
          (error.message || "Bilinmeyen hata")
      );
      console.error("Error deleting task:", error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (subTasks.length > 0) {
      scrollToBottom();
    }
  }, [subTasks]);

  const renderContent = (content) => {
    if (!content) {
      return (
        <Typography
          variant="body1"
          sx={{ color: "#78909C", fontStyle: "italic" }}
        >
          İçerik bulunamadı
        </Typography>
      );
    }

    // Base64 veya URL içeren görselleri işle
    const imgRegex = /<img[^>]+src="([^">]+)"[^>]*>/g;
    let lastIndex = 0;
    const elements = [];
    let match;

    while ((match = imgRegex.exec(content)) !== null) {
      // Görsel öncesi metni ekle
      if (match.index > lastIndex) {
        elements.push(
          <Typography
            key={`text-${lastIndex}`}
            variant="body1"
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {content.slice(lastIndex, match.index)}
          </Typography>
        );
      }

      // Görseli ekle
      const imageUrl = match[1];
      if (imageUrl.startsWith("data:image") || imageUrl.startsWith("http")) {
        elements.push(
          <ImagePreview
            key={`img-${match.index}`}
            src={imageUrl}
            alt="Mesaj görseli"
            onClick={() => setSelectedImage(imageUrl)}
          />
        );
      }

      lastIndex = match.index + match[0].length;
    }

    // Kalan metni ekle
    if (lastIndex < content.length) {
      elements.push(
        <Typography
          key={`text-${lastIndex}`}
          variant="body1"
          sx={{ whiteSpace: "pre-wrap" }}
        >
          {content.slice(lastIndex)}
        </Typography>
      );
    }

    return elements;
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageTag = `<img src="${reader.result}" alt="Yüklenen görsel" />`;
        setReplyText((prev) => (prev ? `${prev}\n${imageTag}` : imageTag));
      };
      reader.readAsDataURL(file);
    }
  };

  if (loadingDetail) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            height: "85vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#FAFBFF",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
            bgcolor: "white",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#1E88E5" }}
              >
                {taskDetail?.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#90CAF9",
                  bgcolor: "#E3F2FD",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "12px",
                  fontWeight: 500,
                }}
              >
                #{taskDetail?.id}
              </Typography>
            </Box>
            {taskDetail?.state && (
              <Chip
                label={
                  taskStateConfig[taskDetail.state]?.label || "Bilinmeyen Durum"
                }
                sx={{
                  color: taskStateConfig[taskDetail.state]?.color || "#757575",
                  bgcolor:
                    taskStateConfig[taskDetail.state]?.bgColor || "#F5F5F5",
                  fontWeight: 500,
                  height: "24px",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isAdmin && (
              <IconButton
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  opacity: 0.7,
                  "&:hover": {
                    opacity: 1,
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <Delete />
              </IconButton>
            )}
            <IconButton
              onClick={onClose}
              sx={{
                "&:hover": { transform: "scale(1.05)" },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Messages Container */}
        <DialogContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            p: 3,
            overflowY: "auto",
            gap: 3,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
              "&:hover": {
                background: "#a8a8a8",
              },
            },
          }}
        >
          {/* Main Task Content */}
          {taskDetail && (
            <MessageContainer isMainUser={taskDetail.userId === userId}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Avatar
                  src={taskDetail.user?.avatarUrl}
                  alt={taskDetail.user?.name}
                  sx={{ width: 24, height: 24 }}
                />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "#1A237E" }}>
                    {user.userFullName || "Kullanıcı"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#78909C" }}>
                    <AccessTime sx={{ fontSize: 12, mr: 0.5, mb: -0.25 }} />
                    {new Date(taskDetail.createDate).toLocaleString("tr-TR")}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ ml: 4 }}>
                {renderContent(taskDetail.content || taskDetail.detail)}
              </Box>
            </MessageContainer>
          )}

          {/* Divider between main task and subtasks */}
          {taskDetail && subTasks.length > 0 && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                my: 1,
              }}
            >
              <Box
                sx={{ flex: 1, height: "1px", bgcolor: "rgba(0, 0, 0, 0.08)" }}
              />
              <Typography variant="caption" sx={{ px: 2, color: "#78909C" }}>
                Yanıtlar ({subTasks.length})
              </Typography>
              <Box
                sx={{ flex: 1, height: "1px", bgcolor: "rgba(0, 0, 0, 0.08)" }}
              />
            </Box>
          )}

          {/* Sub Messages */}
          {subTasks.map((subTask) => (
            <MessageContainer
              key={subTask.id}
              isMainUser={subTask.userId === userId}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Avatar
                  src={subTask.user?.avatarUrl}
                  alt={subTask.user?.name}
                  sx={{ width: 24, height: 24 }}
                />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "#1A237E" }}>
                    {subTask.userId === userId?user.userFullName:   "Pazaryeri Converter"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#78909C" }}>
                    <AccessTime sx={{ fontSize: 12, mr: 0.5, mb: -0.25 }} />
                    {new Date(subTask.createDate).toLocaleString("tr-TR")}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ ml: 4 }}>{renderContent(subTask.detail)}</Box>
            </MessageContainer>
          ))}
          <div ref={messagesEndRef} />
        </DialogContent>

        {/* Reply Input */}
        <Box
          sx={{
            p: 2.5,
            borderTop: "1px solid rgba(0, 0, 0, 0.06)",
            bgcolor: "white",
            boxShadow: "0 -2px 4px rgba(0, 0, 0, 0.02)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Mesajınızı yazın..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              disabled={sending}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#FAFBFF",
                  borderRadius: "12px",
                  "&:hover": {
                    "& > fieldset": {
                      borderColor: "#90CAF9",
                    },
                  },
                  "&.Mui-focused": {
                    "& > fieldset": {
                      borderColor: "#1E88E5",
                    },
                  },
                },
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleImageSelect}
                disabled={sending}
              />
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending}
                startIcon={<Image />}
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  borderColor: "#90CAF9",
                  color: "#1E88E5",
                  "&:hover": {
                    borderColor: "#1E88E5",
                    bgcolor: "rgba(30, 136, 229, 0.04)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                Görsel Ekle
              </Button>
              <Button
                variant="contained"
                disabled={!replyText.trim() || sending}
                onClick={handleSubmitReply}
                sx={{
                  minWidth: "120px",
                  bgcolor: "#1E88E5",
                  borderRadius: "12px",
                  "&:hover": {
                    bgcolor: "#1976D2",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                  textTransform: "none",
                  boxShadow: "0 2px 8px rgba(30, 136, 229, 0.2)",
                  transition: "all 0.2s ease-in-out",
                }}
                endIcon={sending ? <CircularProgress size={20} /> : <Send />}
              >
                {sending ? "Gönderiliyor..." : "Gönder"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* Image Preview Modal */}
      <Modal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            p: 1,
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              bgcolor: "rgba(255, 255, 255, 0.8)",
              "&:hover": { bgcolor: "rgba(255, 255, 255, 0.9)" },
              zIndex: 1,
            }}
            onClick={() => setSelectedImage(null)}
          >
            <Close />
          </IconButton>
          <img
            src={selectedImage}
            alt="Tam boyut görüntü"
            style={{
              maxWidth: "100%",
              maxHeight: "85vh",
              objectFit: "contain",
            }}
          />
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {"Talebi silmek istediğinizden emin misiniz?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu işlem geri alınamaz. Talep ve tüm yanıtları kalıcı olarak
            silinecektir.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            İptal
          </Button>
          <Button
            onClick={handleDeleteTask}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
            sx={{ textTransform: "none" }}
          >
            {deleting ? "Siliniyor..." : "Sil"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

TaskDetail.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string,
    content: PropTypes.string,
    detail: PropTypes.string,
    createdAt: PropTypes.string,
    createDate: PropTypes.string,
    updateDate: PropTypes.string,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    state: PropTypes.number,
    replyCount: PropTypes.number,
    user: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      avatarUrl: PropTypes.string,
    }),
    replies: PropTypes.array,
  }).isRequired,
  onTaskDeleted: PropTypes.func,
};

export default TaskDetail;
